import { runInAction } from 'mobx'
import axiosService from '../../../../../utils/AxiosService'
import { currencyFormat, separateThousands } from '../../../../../utils/Helper'
import Action from '../../../../../models/Action'
import ActionInsight from '../../../../../models/ActionInsight'
import CampaignScenarioActionGroup from '../../../../../models/CampaignScenarioActionGroup'
import {
  ACTION_TYPE_TO_AUTH_PROVIDER,
  normalizeAuthProvider,
  REELS_LIKE_SPLIT_SAVE_COLS,
  REELS_LIKE_TABLE_KEY_TO_ACTION_TYPE,
  SCOPE_KEY_SYNC_CONFIG,
} from './BudgetDetailsPanelConfig'
import {
  fixed2,
  parseGermanBudgetNumber,
  splitPayoutTotalFromInput,
  splitTotalIntoIntegerParts,
} from './BudgetDetailsPanelNumberUtils'
import {
  actionsForReelsTableRow,
  aggregateReelsLikeMetrics,
  findCustomFieldValueForInfluencer,
  influencerMediaActionsForBuyouts,
} from './BudgetDetailsPanelRowBuilders'

function actionInsightSplitPayload(actionType, partInt) {
  if (actionType === 'instagram_story' || actionType === 'tiktok_video') {
    return { plays: partInt }
  }
  return { views: partInt }
}

export async function persistReelsLikeSplitAcrossActions({
  campaignId,
  scenarioId,
  campaignScenario,
  tableKey,
  influencerId,
  slotNumber = 1,
  colKey,
  rawCellValue,
}) {
  const actions = actionsForReelsTableRow(campaignScenario, tableKey, influencerId, slotNumber)
  if (!actions.length) return

  const actionType = REELS_LIKE_TABLE_KEY_TO_ACTION_TYPE[tableKey]
  const baseUrl = `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/actions`

  if (colKey === 'impression') {
    const total = Math.round(parseGermanBudgetNumber(rawCellValue))
    if (!Number.isFinite(total)) {
      const err = new Error('Invalid impression')
      err.data = { error: 'Invalid impression' }
      throw err
    }
    const parts = splitTotalIntoIntegerParts(total, actions.length)
    const results = await Promise.all(
      actions.map((a, i) =>
        axiosService.patch(`${baseUrl}/${a.id}`, { campaign_scenario_action: { impression: parts[i] } })
      )
    )
    runInAction(() => {
      actions.forEach((a, i) => {
        a.impression = results[i].impression
      })
    })
    return
  }

  if (colKey === 'payout') {
    const parts = splitPayoutTotalFromInput(rawCellValue, actions.length)
    if (!parts.length || parts.some(p => !Number.isFinite(p))) {
      const err = new Error('Invalid payout amount')
      err.data = { error: 'Invalid payout amount' }
      throw err
    }
    const results = await Promise.all(
      actions.map((a, i) =>
        axiosService.patch(`${baseUrl}/${a.id}`, {
          campaign_scenario_action: { influencer_payout_price: parts[i] },
        })
      )
    )
    runInAction(() => {
      actions.forEach((a, i) => {
        a.influencer_payout_price = results[i].influencer_payout_price
      })
    })
    return
  }

  if (colKey === 'views') {
    const total = Math.round(parseGermanBudgetNumber(rawCellValue))
    if (!Number.isFinite(total)) {
      const err = new Error('Invalid views')
      err.data = { error: 'Invalid views' }
      throw err
    }
    const parts = splitTotalIntoIntegerParts(total, actions.length)
    const [insightResults, actionResults] = await Promise.all([
      Promise.all(
        actions.map((a, i) => {
          const payload = actionInsightSplitPayload(actionType, parts[i])
          const insightId = a.action_insight?.id
          if (insightId) {
            return axiosService.patch(
              `/api/project_manager/campaigns/${campaignId}/actions/${a.id}/action_insights/${insightId}`,
              { action_insight: payload }
            )
          }
          return axiosService.post(
            `/api/project_manager/campaigns/${campaignId}/actions/${a.id}/action_insights`,
            { action_insight: payload }
          )
        })
      ),
      // Gesamt Impression = quantity * views (Excel formula G): keep it in sync with Views automatically.
      Promise.all(
        actions.map((a, i) => axiosService.patch(`${baseUrl}/${a.id}`, { campaign_scenario_action: { impression: parts[i] } }))
      ),
    ])
    runInAction(() => {
      actions.forEach((a, i) => {
        const r = insightResults[i]
        if (r?.id) a.action_insight = new ActionInsight(r)
        a.impression = actionResults[i].impression
      })
    })
  }
}

function scopeActionsForSync(campaignScenario, influencerId, actionTypes, slotNumber = 1) {
  const iid = Number(influencerId)
  const slot = Number(slotNumber) || 1
  return (campaignScenario?.actions || [])
    .filter(
      a =>
        actionTypes.includes(a.action_type) &&
        Number(a.influencer_id) === iid &&
        (Number(a.slot_number) || 1) === slot &&
        a.visible_in_budget !== false
    )
    .sort((a, b) => Number(a.id) - Number(b.id))
}

function pickAuthenticationIdForActionType(influencer, actionType) {
  const want = ACTION_TYPE_TO_AUTH_PROVIDER[actionType]
  if (!want) return null
  for (const auth of influencer?.authentications || []) {
    if (normalizeAuthProvider(auth.provider) === want) return auth.id
  }
  return null
}

export async function saveOtherChannelLabelForInfluencer({
  campaignId,
  scenarioId,
  campaignScenario,
  influencerId,
  slotNumber = 1,
  label,
}) {
  const cleanLabel = String(label || '').trim()
  const group = await ensureMediaActionGroupForInfluencer(
    campaignId,
    scenarioId,
    campaignScenario,
    influencerId,
    'other_item',
    slotNumber
  )
  if (!group?.id) return null

  const url = `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/campaign_scenario_action_groups/${group.id}`
  const updated = await axiosService.patch(url, {
    campaign_scenario_action_group: { label: cleanLabel || null },
  })

  runInAction(() => {
    const existing = campaignScenario.campaign_scenario_action_groups.find(ag => Number(ag.id) === Number(updated.id))
    if (existing) {
      existing.label = updated.label
    }
    ;(campaignScenario?.actions || [])
      .filter(
        a =>
          a.action_type === 'other_item' &&
          Number(a.influencer_id) === Number(influencerId) &&
          Number(a.campaign_scenario_action_group_id) === Number(updated.id)
      )
      .forEach((action, index, actions) => {
        const base = updated.label || 'Other'
        action.name = actions.length > 1 ? `${base} ${index + 1}` : base
      })
  })

  return updated
}

export async function saveOtherActionGroupReach({
  campaignId,
  scenarioId,
  campaignScenario,
  influencerId,
  slotNumber = 1,
  reach,
}) {
  const group = await ensureMediaActionGroupForInfluencer(
    campaignId,
    scenarioId,
    campaignScenario,
    influencerId,
    'other_item',
    slotNumber
  )
  if (!group?.id) return null

  const url = `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/campaign_scenario_action_groups/${group.id}`
  const updated = await axiosService.patch(url, {
    campaign_scenario_action_group: { reach },
  })

  runInAction(() => {
    const existing = campaignScenario.campaign_scenario_action_groups.find(ag => Number(ag.id) === Number(updated.id))
    if (existing) existing.reach = updated.reach
    ;(campaignScenario?.actions || [])
      .filter(
        a =>
          a.action_type === 'other_item' &&
          Number(a.influencer_id) === Number(influencerId) &&
          Number(a.campaign_scenario_action_group_id) === Number(updated.id)
      )
      .forEach(action => {
        action.reach = updated.reach
      })
  })

  return updated
}

export async function saveManualAuthenticationForInfluencer({ campaignId, influencer, provider, username, reach }) {
  const cleanUsername = String(username || '').trim()
  if (!cleanUsername) return null

  return axiosService.post(`/api/project_manager/campaigns/${campaignId}/authentications`, {
    authentication: {
      provider,
      username: cleanUsername,
      influencer_id: influencer.id,
      reach,
    },
  })
}

export async function saveAuthenticationReach(authenticationId, reach) {
  return axiosService.patch(`/api/project_manager/authentications/${authenticationId}`, {
    authentication: { reach },
  })
}

async function ensureTotalSocialGroupForInfluencer(campaignId, scenarioId, campaignScenario, influencerId, slotNumber = 1) {
  const iid = Number(influencerId)
  const slot = Number(slotNumber) || 1
  const url = `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/campaign_scenario_action_groups`
  const existing = campaignScenario.campaign_scenario_action_groups.find(
    g => g.action_type === 'total_social' && Number(g.influencer_id) === iid && (Number(g.slot_number) || 1) === slot
  )
  if (existing) return
  const ag = await axiosService.post(url, {
    campaign_scenario_action_group: { influencer_id: iid, action_type: 'total_social', slot_number: slot },
  })
  runInAction(() => {
    campaignScenario.campaign_scenario_action_groups.push(new CampaignScenarioActionGroup(ag))
  })
}

async function ensureMediaActionGroupForInfluencer(campaignId, scenarioId, campaignScenario, influencerId, actionType, slotNumber = 1) {
  const iid = Number(influencerId)
  const slot = Number(slotNumber) || 1
  const url = `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/campaign_scenario_action_groups`
  let group = campaignScenario.campaign_scenario_action_groups.find(
    g => g.action_type === actionType && Number(g.influencer_id) === iid && (Number(g.slot_number) || 1) === slot
  )
  if (group) return group
  const ag = await axiosService.post(url, {
    campaign_scenario_action_group: { influencer_id: iid, action_type: actionType, visible_in_budget: true, slot_number: slot },
  })
  runInAction(() => {
    campaignScenario.campaign_scenario_action_groups.push(new CampaignScenarioActionGroup(ag))
  })
  return campaignScenario.campaign_scenario_action_groups.find(
    g => g.action_type === actionType && Number(g.influencer_id) === iid && (Number(g.slot_number) || 1) === slot
  )
}

async function ensureInfluencerVisibleInBudget({
  campaignId,
  scenarioId,
  campaignScenario,
  influencerId,
  slotNumber = 1,
}) {
  const iid = Number(influencerId)
  const slot = Number(slotNumber) || 1
  const groups = (campaignScenario?.campaign_scenario_action_groups || []).filter(
    ag => Number(ag.influencer_id) === iid && (Number(ag.slot_number) || 1) === slot
  )
  if (!groups.length) return

  const needsUpdate = groups.some(ag => ag.visible_in_budget === false)
  if (!needsUpdate) return

  const urlBase = `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/campaign_scenario_action_groups`
  const updatedGroups = await Promise.all(
    groups.map(group =>
      axiosService.patch(`${urlBase}/${group.id}`, {
        campaign_scenario_action_group: { visible_in_budget: true },
      })
    )
  )

  runInAction(() => {
    updatedGroups.forEach(updated => {
      const group = campaignScenario.campaign_scenario_action_groups.find(ag => Number(ag.id) === Number(updated.id))
      if (group) group.visible_in_budget = true
      ;(campaignScenario?.actions || [])
        .filter(a => Number(a.campaign_scenario_action_group_id) === Number(updated.id))
        .forEach(action => {
          action.visible_in_budget = true
        })
    })
  })
}

async function redistributeReelsLikeSplitSaveColsAfterScopeChange({
  campaignId,
  scenarioId,
  campaignScenario,
  splitTableKeys,
  influencerId,
  slotNumber,
  metricsByTable,
  currencySymbol,
}) {
  const sym = currencySymbol || '€'
  for (const tableKey of splitTableKeys) {
    const m = metricsByTable[tableKey]
    if (!m) continue
    const actions = actionsForReelsTableRow(campaignScenario, tableKey, influencerId, slotNumber)
    if (!actions.length) continue
    for (const colKey of REELS_LIKE_SPLIT_SAVE_COLS) {
      let raw
      if (colKey === 'impression') {
        raw = separateThousands(Math.round(m.sumImpression) || 0)
      } else if (colKey === 'views') {
        raw = m.sumViews > 0 ? separateThousands(Math.round(m.sumViews)) : '0'
      } else {
        raw = currencyFormat(fixed2(m.sumPayout), sym)
      }
      await persistReelsLikeSplitAcrossActions({
        campaignId,
        scenarioId,
        campaignScenario,
        tableKey,
        influencerId,
        slotNumber,
        colKey,
        rawCellValue: raw,
      })
    }
  }
}

/**
 * Sync overview scope counts to actions: create/delete as needed, then redistribute
 * aggregated impression / views / payout across the relevant budget tabs.
 */
export async function syncOverviewScopeColumnToScenario({
  campaignId,
  scenarioId,
  campaignScenario,
  influencers,
  influencerId,
  slotNumber = 1,
  colKey,
  targetCount,
  currencySymbol,
}) {
  const cfg = SCOPE_KEY_SYNC_CONFIG[colKey]
  if (!cfg) return

  const infl = influencers?.find(i => Number(i.id) === Number(influencerId))
  if (!infl) throw new Error('Influencer not found')

  const target = Math.max(0, Math.round(Number(targetCount)) || 0)
  const currentActions = scopeActionsForSync(campaignScenario, influencerId, cfg.actionTypes, slotNumber)
  const diff = target - currentActions.length

  const metricsByTable = {}
  for (const tk of cfg.splitTableKeys) {
    const acts = actionsForReelsTableRow(campaignScenario, tk, influencerId, slotNumber)
    metricsByTable[tk] = aggregateReelsLikeMetrics(acts)
  }

  if (diff === 0) return

  const actionsBase = `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/actions`

  await ensureTotalSocialGroupForInfluencer(campaignId, scenarioId, campaignScenario, influencerId, slotNumber)

  if (diff > 0) {
    const createdActions = []
    for (let k = 0; k < diff; k++) {
      const actionType = cfg.actionTypes[0]
      const group = await ensureMediaActionGroupForInfluencer(
        campaignId,
        scenarioId,
        campaignScenario,
        influencerId,
        actionType,
        slotNumber
      )

      if (cfg.noAuthentication) {
        const label = group?.label || ''
        const created = await axiosService.post(actionsBase, {
          influencer_id: infl.id,
          provider: 'other',
          action_type: actionType,
          name: label,
          campaign_scenario_action_group_id: group.id,
        })
        createdActions.push(new Action(created))
        continue
      }

      const authId = pickAuthenticationIdForActionType(infl, actionType)
      if (authId == null) {
        throw new Error('No matching channel authentication for this influencer')
      }
      const auth = infl.authentications?.find(a => Number(a.id) === Number(authId))
      const created = await axiosService.post(actionsBase, {
        influencer_id: infl.id,
        provider: auth?.provider,
        action_type: actionType,
        name: '',
        campaign_scenario_action_group_id: group.id,
        authentication_id: authId,
      })
      createdActions.push(new Action(created))
    }
    if (createdActions.length) {
      runInAction(() => {
        campaignScenario.actions.push(...createdActions)
      })
      await ensureInfluencerVisibleInBudget({
        campaignId,
        scenarioId,
        campaignScenario,
        influencerId,
        slotNumber,
      })
    }
  } else {
    const victims = currentActions.slice(diff)
    const victimIds = victims.map(victim => Number(victim.id))
    for (const victim of victims) {
      await axiosService.delete(`${actionsBase}/${victim.id}`)
    }
    if (victimIds.length) {
      runInAction(() => {
        campaignScenario.actions.splice(
          0,
          campaignScenario.actions.length,
          ...campaignScenario.actions.filter(a => !victimIds.includes(Number(a.id)))
        )
      })
    }
  }

  await redistributeReelsLikeSplitSaveColsAfterScopeChange({
    campaignId,
    scenarioId,
    campaignScenario,
    splitTableKeys: cfg.splitTableKeys,
    influencerId,
    slotNumber,
    metricsByTable,
    currencySymbol,
  })
}

export async function persistBuyoutFieldsForInfluencer({
  campaignId,
  scenarioId,
  campaignScenario,
  influencerId,
  slotNumber = 1,
  colKey,
  rawValue,
}) {
  const actions = influencerMediaActionsForBuyouts(campaignScenario, influencerId, slotNumber)
  if (!actions.length) {
    const err = new Error('No content actions')
    err.data = { error: 'Add content pieces in Overview first (e.g. Reels, Posts)' }
    throw err
  }

  const patchAction = (action, body) =>
    axiosService.patch(
      `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/actions/${action.id}`,
      { campaign_scenario_action: body }
    )

  let patches = []

  if (colKey === 'anzahlBuyout') {
    const targetCount = Math.max(0, parseInt(String(rawValue).replace(/\D/g, ''), 10) || 0)
    const capped = Math.min(targetCount, actions.length)
    patches = actions.map((action, index) => {
      const enable = index < capped
      return patchAction(action, enable ? { buyout: true } : { buyout: false, buyout_price: 0 })
    })
  } else if (colKey === 'buyoutUnitPrice') {
    const price = parseGermanBudgetNumber(rawValue)
    if (!Number.isFinite(price) || price < 0) {
      const err = new Error('Invalid price')
      err.data = { error: 'Invalid price' }
      throw err
    }
    const rounded = Math.round(price)
    const buyoutActions = actions.filter(a => a.buyout)
    if (!buyoutActions.length) {
      patches = [patchAction(actions[0], { buyout: true, buyout_price: rounded })]
    } else {
      patches = buyoutActions.map(action => patchAction(action, { buyout: true, buyout_price: rounded }))
    }
  } else if (colKey === 'welcheBuyouts') {
    const text = rawValue == null ? '' : String(rawValue)
    const buyoutActions = actions.filter(a => a.buyout)
    const target = buyoutActions[0] || actions[0]
    if (!target) return []
    patches = [patchAction(target, { notes: text })]
  } else {
    return []
  }

  const updatedActions = await Promise.all(patches)

  runInAction(() => {
    updatedActions.forEach(updated => {
      const current = campaignScenario.actions.find(a => Number(a.id) === Number(updated.id))
      if (!current) return
      if (updated.buyout != null) current.buyout = !!updated.buyout
      if (updated.buyout_price != null) current.buyout_price = updated.buyout_price
      if (updated.notes != null) current.notes = updated.notes
    })
  })

  return updatedActions
}

export async function persistOverallKostenMargins({
  campaignId,
  scenarioId,
  campaignScenario,
  actions,
  colKey,
  parsed,
}) {
  const patchKey = colKey === 'margeBuyouts' ? 'influencer_payout_margin' : 'advertiser_margin'
  const updatedActions = await Promise.all(
    actions.map(a =>
      axiosService.patch(
        `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/actions/${a.id}`,
        { campaign_scenario_action: { [patchKey]: parsed } }
      )
    )
  )

  runInAction(() => {
    updatedActions.forEach(updated => {
      const current = campaignScenario.actions.find(a => Number(a.id) === Number(updated.id))
      if (!current) return
      if (updated.advertiser_margin != null) current.advertiser_margin = updated.advertiser_margin
      if (updated.influencer_payout_margin != null) current.influencer_payout_margin = updated.influencer_payout_margin
      if (updated.influencer_payout_price != null) current.influencer_payout_price = updated.influencer_payout_price
      if (updated.impression != null) current.impression = updated.impression
      if (updated.buyout_price != null) current.buyout_price = updated.buyout_price
      if (updated.buyout != null) current.buyout = !!updated.buyout
      if (updated.ksa_tax_enabled != null) current.ksa_tax_enabled = !!updated.ksa_tax_enabled
      if (updated.notes != null) current.notes = updated.notes
    })
  })
}

export async function saveCampaignCustomFieldValueRequest({
  campaignId,
  scenarioId,
  campaignScenario,
  influencerId,
  slotNumber = 1,
  field,
  rawValue,
}) {
  const existing = findCustomFieldValueForInfluencer(campaignScenario, influencerId, field.id, slotNumber)
  const isNumeric = (field.fieldType || field.field_type) === 'numeric'
  const str = rawValue == null ? '' : String(rawValue).trim()

  let bodyInner
  if (isNumeric) {
    if (str === '') {
      bodyInner = { text_value: null, numeric_value: null }
    } else {
      const n = Number(str.replace(/\s/g, '').replace(',', '.'))
      if (!Number.isFinite(n)) {
        const err = new Error('Invalid number')
        err.data = { error: 'Invalid number' }
        throw err
      }
      bodyInner = { text_value: null, numeric_value: n }
    }
  } else {
    bodyInner = { text_value: str === '' ? null : String(rawValue), numeric_value: null }
  }

  const base = `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/campaign_custom_field_values`

  if (existing?.id) {
    return axiosService.patch(`${base}/${existing.id}`, { campaign_custom_field_value: bodyInner })
  }

  return axiosService.post(base, {
    campaign_custom_field_value: {
      influencer_id: influencerId,
      campaign_custom_field_id: field.id,
      slot_number: slotNumber,
      ...bodyInner,
    },
  })
}

/** Saves one of the two editable cost_item sub-fields (quantity or unit_price); the other keeps its current value. */
export async function saveCostItemFieldValueRequest({
  campaignId,
  scenarioId,
  campaignScenario,
  influencerId,
  slotNumber = 1,
  field,
  attr,
  rawValue,
}) {
  const existing = findCustomFieldValueForInfluencer(campaignScenario, influencerId, field.id, slotNumber)
  const str = rawValue == null ? '' : String(rawValue).trim()
  const n = str === '' ? null : Number(str.replace(/\s/g, '').replace(',', '.'))
  if (str !== '' && !Number.isFinite(n)) {
    const err = new Error('Invalid number')
    err.data = { error: 'Invalid number' }
    throw err
  }

  const bodyInner = { [attr]: n }
  const base = `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/campaign_custom_field_values`

  if (existing?.id) {
    return axiosService.patch(`${base}/${existing.id}`, { campaign_custom_field_value: bodyInner })
  }

  return axiosService.post(base, {
    campaign_custom_field_value: {
      influencer_id: influencerId,
      campaign_custom_field_id: field.id,
      slot_number: slotNumber,
      ...bodyInner,
    },
  })
}

export async function saveBudgetColumnVisibility(campaignScenario, next) {
  await axiosService.patch(`/api/project_manager/campaigns/${campaignScenario.campaign_id}/campaign_scenarios/${campaignScenario.id}`, {
    campaign_scenario: { budget_table_column_visibility: next },
  })
  campaignScenario.budget_table_column_visibility = next
}

export async function duplicateInfluencerRow({ campaignId, scenarioId, campaignScenario, influencerId }) {
  const url = `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/campaign_scenario_action_groups/duplicate_influencer_row`
  const created = await axiosService.post(url, { influencer_id: influencerId })
  runInAction(() => {
    campaignScenario.campaign_scenario_action_groups.push(new CampaignScenarioActionGroup(created))
  })
  return created
}

/** Deletes an "(extra)" duplicate row (slotNumber > 1), cascading to its content pieces on the backend. */
export async function deleteInfluencerRowSlot({ campaignId, scenarioId, campaignScenario, influencerId, slotNumber }) {
  const url = `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/campaign_scenario_action_groups/delete_influencer_row`
  await axiosService.delete(url, { data: { influencer_id: influencerId, slot_number: slotNumber } })
  runInAction(() => {
    const iid = Number(influencerId)
    const slot = Number(slotNumber) || 1
    campaignScenario.campaign_scenario_action_groups = campaignScenario.campaign_scenario_action_groups.filter(
      g => !(Number(g.influencer_id) === iid && (Number(g.slot_number) || 1) === slot)
    )
    campaignScenario.campaign_custom_field_values = (campaignScenario.campaign_custom_field_values || []).filter(
      v => !(Number(v.influencer_id) === iid && (Number(v.slot_number) || 1) === slot)
    )
    campaignScenario.actions = (campaignScenario.actions || []).filter(
      a => !(Number(a.influencer_id) === iid && (Number(a.slot_number) || 1) === slot)
    )
  })
}
